/* eslint-disable camelcase, no-param-reassign */
// Fetches redirct details from Marketplace based on entity uuid
// Supported product types: Insurance, PL, SMB, Edu

import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import datadog from '@nerdwallet/datadog';

import PRODUCT_TYPES from '../config/marketplace-product-types';
import { getVerticalConfig } from '../config/new-vertical-definitions';
import {
  MARKETPLACE_INVALID_TOKEN_MSG,
  NOTFOUND_ERROR_MSG,
  OverrideError,
} from './errors';
import getErrorHandlers from './error-handling';
import fetchFromMarketplace from './marketplace-sdk';
import { getStaticAffiliateData } from './static-data-sdk';
import { decodeUrl, validateOverrideUrl } from './override-url-utils';
import { validateMarketplaceToken } from './validate-marketplace-token';
import { logWarn } from './logging-utils';
import {
  isFormattedForPartnerTrackingInjection,
  injectPartnerTrackingId,
  updateQueryParams,
  queryParamExists,
} from './partner-tracking-utils';
import { parseUriTemplate } from './uri-template-utils';

/**
 * Helper function to create a destination url
 */
const addExtraPartnerParams = (url, extraPartnerParams) => {
  if (!extraPartnerParams) {
    return url;
  }
  // Append partner params to url
  try {
    extraPartnerParams = decodeURIComponent(extraPartnerParams);
  } catch (e) {
    // Catch malformed URI errors
    if (e.name !== 'URIError') {
      throw e;
    }
  }
  return updateQueryParams(url, extraPartnerParams);
};

// Check that is a valid UUID (version 1 to 5)
const isValidUUID = (id) => {
  const regex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return regex.test(id);
};

/**
 * MAIN FUNCTION
 */
export default async ({ store, res, req, match }) => {
  // Instantiate an error handler
  const { handleInvalidOverride, handleNotFound } = getErrorHandlers({
    req,
    res,
    store,
    service: 'interstitial-client',
  });

  const ROUTE_CATEGORY = 'UUID'; // to distinguish from /redirect/:slug/:id routes
  const id = match.params.uuid;
  if (!isValidUUID(id)) {
    handleNotFound({
      product: {
        category: ROUTE_CATEGORY,
        id,
      },
    });
    throw new Error(NOTFOUND_ERROR_MSG);
  }

  /**
   * Helper function get client and logger data from a Marketplace response
   */
  const getClientAndLoggerData = (response = {}) => {
    const {
      entity_type_name: marketplaceEntityType, // e.g. 'offers'
      name: marketplaceSlug, // e.g. 'Allied - Car Insurance'
      product_type_name: marketplaceProductType, // e.g. 'insurance'
      legacy_id: marketplaceLegacyId, // e.g. "1111"
    } = response;

    datadog.increment(
      'interstitial_client.fetch_redirect_details.marketplace.success',
      1,
      { tags: { redirect_type: 'uuid', product_type: marketplaceProductType } }
    );

    /**
     * ==== Create a url for redirecting to a partner site ====
     */
    const clickId =
      _.get(req, 'query.shopping_finish_id') ||
      _.get(req, 'query.finish_id') ||
      uuidv4().replace(/-/g, '');

    let clickIdPrefix = null;
    let {
      destination_url: destinationUrl,
      partner_tracking_parameter: partnerTrackingParam,
    } = response;
    const { affiliate_id } = response;
    let network = affiliate_id !== null ? affiliate_id : -9999; // same as legacy, required by DWH

    const override = _.get(req, 'query.override');
    if (override) {
      // "override" is a base64 encoded url from the query params
      // It is created when issuers generate a unique link for a user (e.g. for prequal)
      // If it passes validation, it replaces the originally stored destinationUrl
      try {
        let decodedOverrideUrl = null;
        decodedOverrideUrl = decodeUrl(marketplaceProductType, override);
        const whiteList = _.get(
          response,
          'supplemental_data.override_whitelist',
          []
        );
        if (
          validateOverrideUrl(
            marketplaceProductType,
            decodedOverrideUrl,
            whiteList
          )
        ) {
          destinationUrl = decodedOverrideUrl;
          req.log.info({
            message: 'URI overridden by override parameter.',
            context: {
              original_destination_url: destinationUrl,
              override_url: decodedOverrideUrl,
            },
          });
        }
      } catch (e) {
        if (e instanceof OverrideError) {
          handleInvalidOverride({
            product: { category: ROUTE_CATEGORY, id, override },
            error: e.message,
          });
        }
        throw e;
      }
    }

    if (destinationUrl) {
      // apply handlebar template to the URL
      destinationUrl = parseUriTemplate(destinationUrl, req.query);

      let trackingId = clickId;

      // IF formatted destination url (eg: 'www.test-issuer.com?sid=NW_IC_CLICK_ID')
      if (isFormattedForPartnerTrackingInjection(destinationUrl)) {
        destinationUrl = injectPartnerTrackingId(destinationUrl, trackingId);
      } else {
        // If no partner tracking param, find it in affiliate_networks.json
        if (!partnerTrackingParam) {
          const affiliateNetwork = getStaticAffiliateData(
            destinationUrl,
            affiliate_id,
            req
          );

          if (affiliateNetwork) {
            partnerTrackingParam = affiliateNetwork.partnerTrackingParam;
            network = affiliateNetwork.id;
            clickIdPrefix = affiliateNetwork.clickIdPrefix;
          }
        }

        // Append partner tracking param and clickId if present (e.g. subid=<clickId>)
        // Important because this is how NW monetizing partners record user clicks
        if (
          partnerTrackingParam &&
          !queryParamExists(destinationUrl, partnerTrackingParam)
        ) {
          if (clickIdPrefix) {
            trackingId = clickIdPrefix + clickId;
          }
          destinationUrl = updateQueryParams(
            destinationUrl,
            `${partnerTrackingParam}=${trackingId}`
          );
        }
      }

      // Add extra partner params if they exist
      const extraPartnerParams = _.get(req, 'query.partnerParams');
      destinationUrl = addExtraPartnerParams(
        destinationUrl,
        extraPartnerParams
      );
    }

    /**
     * ==== Get value for monetizing and sponsored ====
     */
    let isMonetizable = response.monetizable;
    // TODO: network links also have values of 1 and 0
    if (marketplaceProductType === PRODUCT_TYPES.CREDIT_CARDS) {
      isMonetizable = isMonetizable ? 1 : 0; // Preserves legacy Redirect Service behavior
    }

    /**
     * ==== Get Marketplace values ====
     */
    // TODO: Refactor finish-event and un-nest this
    const marketplaceNestedOffer = response.offer || {};
    const product = response.product || {};
    const institution = response.institution || {};
    marketplaceNestedOffer.product = product;
    marketplaceNestedOffer.product.institution = institution;

    /**
     * ==== Get per product type specific values ====
     */
    const supplementalData = response.supplemental_data || {};
    const supplementalTradeName = supplementalData.trade_name || null;
    const supplementalProductType = supplementalData.product_type || null;

    /**
     * ==== Get vertical config ====
     */
    // TODO: remove get(supplementalData, 'category.value') once Marketplace returns the correct value
    // https://github.com/NerdWallet/marketplace/blob/master/src/marketplace/lib/redirect_link_calculations.py#L190
    const subCategory =
      _.get(supplementalData, 'category.value') || supplementalData.category;
    const verticalConfig = getVerticalConfig(
      marketplaceProductType,
      marketplaceEntityType,
      subCategory
    );

    const clientData = {
      destinationUrl,
      monetizing: isMonetizable,
      slug: marketplaceSlug,
      uniqueClickId: clickId,
      // Used by GTM only
      gtmEvent: verticalConfig.gtmEvent,
      gtmPageTopic: verticalConfig.gtmPageTopic || '',
      productId: marketplaceLegacyId || id,
      quantcastLabel: verticalConfig.quantcastLabel,
      referPath: req.headers.referer,
      vertical: verticalConfig.gtmPageVertical,

      // Used by investing
      advLink: _.get(supplementalData, 'sec_form_adv_url', null),
      productType: _.get(supplementalData, 'product_type', null),
      secName: _.get(supplementalData, 'sec_legal_name', null),
      tradeName: supplementalTradeName,

      // Used for CC card art
      institutionName: institution.name,
      imageSource: marketplaceNestedOffer?.details?.image_source?.value,
    };

    if (marketplaceProductType === PRODUCT_TYPES.CREDIT_CARDS) {
      // Used by CC for credit_card_browse_page_click events
      clientData.marketplaceOfferProductType = marketplaceProductType;
    }

    const loggerData = {
      additionalQueryParams: verticalConfig.trackedQueryParams || [],
      affiliateId: network,
      category: verticalConfig.category,
      productId: marketplaceLegacyId || id,
      monetizing: isMonetizable,
      productSlug: marketplaceSlug,
      sponsored: isMonetizable,
      uniqueClickId: clickId,
      verticalEventFields: verticalConfig.eventFields,
      // Used by investing
      institutionName: supplementalTradeName,
      offerAccountType: _.get(supplementalData, 'offer_account_type', null),
      offerName: _.get(supplementalData, 'offer_name', null),
      offerReviewType: _.get(supplementalData, 'offer_review_type', null),
      productName: _.get(supplementalData, 'product_name', null),
      productType: supplementalProductType,
      verticalProductType: marketplaceProductType,
      // Used by finish-event only
      // TODO: refactor finish-event and put marketplace_offer and url at top level
      product: {
        marketplace_offer: marketplaceNestedOffer,
        url: destinationUrl,
      },
    };

    // Potentially used in investing
    if (verticalConfig.specialDisclaimers) {
      const specialDisclaimer =
        verticalConfig.specialDisclaimers[marketplaceSlug];
      if (specialDisclaimer) {
        clientData.disclaimer = specialDisclaimer;
      }
    }

    // Set other options for non-monetizing investing products
    if (
      !clientData.monetizing &&
      _.get(req, 'prefetchData.investingAlternatives')
    ) {
      clientData.otherOptions = _.get(
        req,
        'prefetchData.investingAlternatives'
      );
      clientData.showOtherOptions = true;
    }

    return {
      clientData,
      loggerData,
    };
  };

  /**
   * Fetch from Marketplace
   */
  const response = await fetchFromMarketplace({
    category: ROUTE_CATEGORY,
    id,
    req,
    res,
    store,
  });

  if (!response) {
    handleNotFound({
      product: {
        category: ROUTE_CATEGORY,
        id,
      },
    });
    throw new Error(NOTFOUND_ERROR_MSG);
  }

  // Flag redirects from Apply Links that weren't generated by Marketplace
  const token = _.get(req, 'query.mvt');
  // TODO: once Marketplace generates a token for all product types, we should
  // remove the check for the token's existence
  if (token) {
    const cookieId = _.get(req, 'cookies.cookie_id');
    const { isValid, tokenCreationTimeInSeconds } = validateMarketplaceToken(
      cookieId,
      id,
      token
    );
    if (!isValid) {
      logWarn(req, MARKETPLACE_INVALID_TOKEN_MSG, {
        token,
        tokenCreationTimeInSeconds,
      });
    }
  }
  const { clientData, loggerData } = getClientAndLoggerData(response);
  return { clientData, loggerData };
};
